// ./src/components/features/ParkingSession/ActiveParkingBanner.tsx
import React, { useMemo, useCallback, useState, useEffect } from "react";
import { Paper, Group, Text, Box, Badge } from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import {
  ParkingSession,
  getSessionRemainingMinutes,
  getSessionElapsedMinutes,
  isSessionExpired,
} from "@/utils/api";
import { AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

export interface ActiveParkingBannerProps {
  activeSessions: ParkingSession[];
  /** Tapping anywhere on the banner opens session details */
  onTap?: (session: ParkingSession) => void;
  loading?: boolean;
}

// ============================================
// Theme
// ============================================

type BannerState = "active" | "warning" | "expired";

const URGENCY_MINUTES = 10;

/** Maps state → CarExpenses palette color + shades */
const THEME: Record<BannerState, { color: string; shade: number }> = {
  active: { color: "brand", shade: 6 },
  warning: { color: "warning", shade: 5 },
  expired: { color: "error", shade: 5 },
};

const getState = (expired: boolean, remaining: number | null): BannerState => {
  if (expired) return "expired";
  if (remaining != null && remaining <= URGENCY_MINUTES) return "warning";
  return "active";
};

// ============================================
// Helpers
// ============================================

const fmtTimer = (min: number | null): string => {
  if (min == null) return "--:--";
  const a = Math.abs(min);
  const h = Math.floor(a / 60);
  const m = a % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
};

const fmtElapsed = (start: string | undefined): string => {
  if (!start) return "--:--";
  const d = dayjs().diff(dayjs(start), "second");
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = d % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
};

const getMostUrgent = (sessions: ParkingSession[]): ParkingSession | null => {
  if (!sessions.length) return null;
  if (sessions.length === 1) return sessions[0];
  return [...sessions].sort((a, b) => {
    const score = (s: ParkingSession) => {
      const rem = getSessionRemainingMinutes(s);
      if (isSessionExpired(s) && rem != null) return rem;
      if (rem != null) return rem;
      return 10000 - (getSessionElapsedMinutes(s) ?? 0);
    };
    return score(a) - score(b);
  })[0];
};

// ============================================
// Component
// ============================================

const ActiveParkingBannerComponent: React.FC<ActiveParkingBannerProps> = ({
  activeSessions,
  onTap,
  //loading = false,
}) => {
  const { t } = useTranslation(["parking", "common"]);

  const session = useMemo(() => getMostUrgent(activeSessions), [activeSessions]);
  const [timer, setTimer] = useState("--:--");

  const expired = useMemo(() => (session ? isSessionExpired(session) : false), [session]);
  const remaining = useMemo(() => (session ? getSessionRemainingMinutes(session) : null), [session]);
  const hasDuration = !!session?.durationMinutes;
  const state = useMemo(() => getState(expired, remaining), [expired, remaining]);
  const { color, shade } = THEME[state];

  const cv = (s: number) => `var(--mantine-color-${color}-${s})`;
  const mc = (s: number) => `${color}.${s}`;

  useEffect(() => {
    if (!session?.startTime) return;
    const tick = () => {
      if (hasDuration) {
        const r = getSessionRemainingMinutes(session);
        setTimer(r != null && r < 0 ? `+${fmtTimer(Math.abs(r))}` : fmtTimer(r));
      } else {
        setTimer(fmtElapsed(session.startTime));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [session, hasDuration]);

  const handleTap = useCallback(() => {
    if (session) onTap?.(session);
  }, [session, onTap]);

  if (!session) return null;

  const total = activeSessions.length;
  const vehicle =
    session.car?.label ||
    t("parking:labels.unknownVehicle", { defaultValue: "Vehicle" });

  const timerSuffix = expired
    ? t("parking:timer.overtime", { defaultValue: "over" })
    : hasDuration
      ? t("parking:timer.remaining", { defaultValue: "left" })
      : null;

  return (
    <Paper
      px="sm"
      py={8}
      radius="md"
      shadow="xs"
      onClick={handleTap}
      style={{
        borderLeft: `3px solid ${cv(shade)}`,
        backgroundColor: cv(0),
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        userSelect: "none",
      }}
    >
      <Group gap={10} justify="space-between" wrap="nowrap">
        {/* Left: icon + vehicle + multi-session badge */}
        <Group gap={8} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "var(--mantine-radius-sm)",
              backgroundColor: cv(shade),
              flexShrink: 0,
            }}
          >
            <AppIcon iconName="ParkingCircle" size={15} color="white" />
          </Box>

          <Text
            size="sm"
            fw={600}
            c={mc(shade + 2)}
            lineClamp={1}
            style={{ minWidth: 0 }}
          >
            {vehicle}
          </Text>

          {total > 1 && (
            <Badge size="xs" color={color} variant="light" circle style={{ flexShrink: 0 }}>
              {total}
            </Badge>
          )}
        </Group>

        {/* Right: timer + suffix + chevron */}
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          <Text
            size="sm"
            fw={700}
            c={mc(shade)}
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {timer}
          </Text>

          {timerSuffix && (
            <Text size="xs" c={mc(shade)} style={{ opacity: 0.65 }}>
              {timerSuffix}
            </Text>
          )}

          <AppIcon
            iconName="ChevronRight"
            size={16}
            color={cv(shade)}
            style={{ opacity: 0.5 }}
          />
        </Group>
      </Group>
    </Paper>
  );
};

export const ActiveParkingBanner = React.memo(ActiveParkingBannerComponent);